/* eslint-disable camelcase */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { UserDO } from 'wdna-components/lib/types.new';
import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';

import { LovDO } from '../../../types';
import { authorization } from '../requests';

import UserInfo from '../components/UserInfo';
import UserPassword from '../components/UserPassword';
import SkinSwitcher from '../components/SkinSwitcher';

interface State {
  loading: boolean;
  areaList: LovDO[];
  countryList: LovDO[];

  data: Partial<UserDO>;
}

export default class Settings extends React.Component<unknown, State> {
  private mounted: boolean;

  public constructor(props: unknown) {
    super(props);

    this.mounted = false;

    this.state = {
      loading: false,
      areaList: [],
      countryList: [],
      data: {}
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    const areaList: LovDO[] = await authorization<LovDO[]>('areas');
    const countryList: LovDO[] = await authorization<LovDO[]>('countries');
    const data: UserDO = await authorization<UserDO>('myinfo', { refresh: true });

    this.setLocalState({
      areaList,
      countryList,
      data,
    });
    this.loadingFinish();
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { data } = this.state;

    this.loadingStart();
    authorization<UserDO>('update', data)?.then(() => {
      messageInfo('Success', 'Data saved successful', 5000);
      this.load().catch(() => { /* pass */ });
    }).catch((error: any) => {
      messageError('Error', getErrorMessage(error));
    }).finally(() => {
      this.loadingFinish();
    });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public loadingStart(): void {
    this.setLocalState({ loading: true });
  }

  public loadingFinish(): void {
    this.setLocalState({ loading: false });
  }

  public render(): JSX.Element {
    const { loading, data, countryList, areaList } = this.state;

    return (
      <div id="content" className="content user-options-panel">
        {/* widget grid */}

        <WidgetGrid>
          {/* START ROW */}

          <div className="row">
            {/* NEW COL START */}
            <article className="col-sm-12 col-md-12 col-lg-6">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                fullscreenbutton={false}
                colorbutton={false}
                deletebutton={false}
              >
                <header>
                  <ul className="nav nav-tabs pull-left in secondary-tabs">
                    <li className="active">
                      <a data-toggle="tab" href="#hr1">
                        <i className="fa fa-user" />
                        <span className="hidden-mobile hidden-tablet"> User info</span>
                      </a>
                    </li>

                    <li>
                      <a data-toggle="tab" href="#hr2">
                        <i className="fa fa-lock" />
                        <span className="hidden-mobile hidden-tablet"> User password</span>
                      </a>
                    </li>

                    <li>
                      <a data-toggle="tab" href="#hr3">
                        <i className="fa fa-cogs" />
                        <span className="hidden-mobile hidden-tablet"> Visual options</span>
                      </a>
                    </li>
                  </ul>
                </header>

                {/* widget content */}
                <div className="widget-body" style={{ padding: 0 }}>
                  <div className="tab-content">
                    <div className="tab-pane active" id="hr1">
                      <UserInfo
                        submit={this.submit}
                        handleChange={this.handleChange}
                        handleSelect2={this.handleChange}
                        loading={loading}
                        areaList={areaList}
                        countryList={countryList}
                        user={data}
                      />
                    </div>
                    <div className="tab-pane" id="hr2">
                      <UserPassword />
                    </div>
                    <div className="tab-pane" id="hr3">
                      <SkinSwitcher />
                    </div>
                  </div>
                </div>
                {/* end widget content */}
              </JarvisWidget>
              {/* end widget */}
            </article>
            {/* END COL */}
          </div>

          {/* END ROW */}
        </WidgetGrid>

        {/* end widget grid */}
      </div>
    );
  }
}
